import { z } from "zod";

// Every enum is a tuple + Zod schema + TS type with the same name.
// Values are stored verbatim in the database (text columns), so renaming one is a migration.

export const UNIT_TYPES = ["foot", "quad", "vehicle", "dog", "horse", "uav"] as const;
export const UnitType = z.enum(UNIT_TYPES);
export type UnitType = z.infer<typeof UnitType>;

/** Unit settings rows also have a mandatory `default` row used for unknown unit types. */
export const UNIT_SETTING_KEYS = [...UNIT_TYPES, "default"] as const;
export const UnitSettingKey = z.enum(UNIT_SETTING_KEYS);
export type UnitSettingKey = z.infer<typeof UnitSettingKey>;

/** Only `searching` builds sector coverage. */
export const PARTICIPANT_STATUSES = [
  "idle",
  "searching",
  "transit",
  "paused",
  "returning",
  "off_duty",
  "sos",
] as const;
export const ParticipantStatus = z.enum(PARTICIPANT_STATUSES);
export type ParticipantStatus = z.infer<typeof ParticipantStatus>;
/**
 * Statuses in which the phone records the track; HQ expects fresh positions from them. Every
 * status but "off duty": HQ sees everyone in the action, also those who forgot to pick a status
 * (owner, 2026-10-11, #212). Only `searching` builds coverage.
 */
export const TRACKED_STATUSES: readonly ParticipantStatus[] = [
  "idle",
  "searching",
  "transit",
  "paused",
  "returning",
  "sos",
];

/** "hq": HQ in the field, with the HQ tab in the app (sectors, assignments, groups; issue #182). */
export const PARTICIPANT_ROLES = ["searcher", "leader", "commander", "hq"] as const;
export const ParticipantRole = z.enum(PARTICIPANT_ROLES);
export type ParticipantRole = z.infer<typeof ParticipantRole>;

export const ACTION_KINDS = ["real", "exercise"] as const;
export const ActionKind = z.enum(ACTION_KINDS);
export type ActionKind = z.infer<typeof ActionKind>;

export const ACTION_STATUSES = ["planned", "active", "suspended", "closed"] as const;
export const ActionStatus = z.enum(ACTION_STATUSES);
export type ActionStatus = z.infer<typeof ActionStatus>;

export const SECTOR_STATUSES = [
  "unassigned",
  "assigned",
  "in_progress",
  "completed",
  "attention",
] as const;
export const SectorStatus = z.enum(SECTOR_STATUSES);
export type SectorStatus = z.infer<typeof SectorStatus>;

export const ASSIGNMENT_RESULTS = ["clear", "found", "partial", "aborted"] as const;
export const AssignmentResult = z.enum(ASSIGNMENT_RESULTS);
export type AssignmentResult = z.infer<typeof AssignmentResult>;

export const FINDING_CATEGORIES = ["trace", "item", "witness", "hazard", "other"] as const;
export const FindingCategory = z.enum(FINDING_CATEGORIES);
export type FindingCategory = z.infer<typeof FindingCategory>;

export const TRACKING_STATES = ["running", "stopped_by_user", "killed", "unknown"] as const;
export const TrackingState = z.enum(TRACKING_STATES);
export type TrackingState = z.infer<typeof TrackingState>;

export const NETWORK_STATES = ["online", "offline", "limited"] as const;
export const NetworkState = z.enum(NETWORK_STATES);
export type NetworkState = z.infer<typeof NetworkState>;

export const POSITION_SOURCES = ["phone", "lora", "inreach", "manual"] as const;
export const PositionSource = z.enum(POSITION_SOURCES);
export type PositionSource = z.infer<typeof PositionSource>;

/** Staff roles inside an organization (panel accounts). */
export const STAFF_ROLES = ["admin", "commander", "observer"] as const;
export const StaffRole = z.enum(STAFF_ROLES);
export type StaffRole = z.infer<typeof StaffRole>;

export const TRACKING_MODES = ["normal", "live", "battery_saver"] as const;
export const TrackingMode = z.enum(TRACKING_MODES);
export type TrackingMode = z.infer<typeof TrackingMode>;

export const SESSION_STATES = [
  "active",
  "pending_approval",
  "grace",
  "revoked",
  "action_closed",
] as const;
export const SessionState = z.enum(SESSION_STATES);
export type SessionState = z.infer<typeof SessionState>;

export const PLATFORMS = ["android", "ios", "web", "simulator"] as const;
export const Platform = z.enum(PLATFORMS);
export type Platform = z.infer<typeof Platform>;
